import { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Badge,
  Alert,
  Tabs,
  Tab,
  Button,
  Modal,
  Collapse,
  Tooltip,
  OverlayTrigger,
} from "react-bootstrap";
import { ProcessUserCreationData } from "./utils/user-utils-func";

// Change Indicator Component
const ChangeIndicator = ({ changeType, showLabel = true }) => {
  const config = {
    ADDED: {
      color: "success",
      icon: "fa-plus",
      label: "Added",
      bgClass: "bg-success text-white",
    },
  };

  const { icon, label, bgClass } = config[changeType] || config.ADDED;

  const hasIcon = icon !== null;
  const hasLabel = showLabel && label;

  return (
    <span
      className={`badge ${bgClass}`}
      style={{
        fontSize: "0.7rem",
        padding: hasLabel ? "0.25rem 0.5rem" : "0.25rem",
        minWidth: hasIcon && !hasLabel ? "24px" : "auto",
      }}
    >
      {hasIcon && (
        <i
          className={`fa ${icon} ${hasLabel ? "mr-1" : ""}`}
          style={{ fontSize: "10px" }}
        ></i>
      )}
      {hasLabel && label}
    </span>
  );
};

// Detail Item Component
const DetailItem = ({
  icon,
  label,
  value,
  color = "text-base",
  tooltip,
  status,
}) => {
  const getStatusIcon = (value) =>
    value ? "fa-times text-danger" : "fa-check text-success";

  const tooltipElement = tooltip ? (
    <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
      <i
        className="fa fa-info-circle ml-1"
        style={{ opacity: 0.6, fontSize: "12px" }}
      ></i>
    </OverlayTrigger>
  ) : null;

  return (
    <Col xs={12} sm={6} md={4} className="mb-3">
      <div className="d-flex align-items-start">
        <div className="mr-2 mt-1 primary-color">
          <i className={`fa ${icon}`} style={{ fontSize: "14px" }}></i>
        </div>
        <div className="flex-grow-1">
          <div className="d-flex align-items-center mb-1">
            <small
              className="text-muted font-weight-bold text-uppercase"
              style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}
            >
              {label}
            </small>
            {tooltipElement}
          </div>
          <div className="d-flex align-items-center">
            {status !== undefined && (
              <i
                className={`fa ${getStatusIcon(status)} mr-1`}
                style={{ fontSize: "12px" }}
              ></i>
            )}
            <span
              className={`font-weight-bold ${color}`}
              style={{ fontSize: "0.875rem", wordBreak: "break-word" }}
            >
              {value || "—"}
            </span>
          </div>
        </div>
      </div>
    </Col>
  );
};

// AccessChipList Component
const AccessChipList = ({
  items,
  color = "primary",
  maxVisible = 3,
  title,
  changeMap = {},
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  const getChangeType = (itemId) => changeMap[itemId] || "ADDED";

  const visibleItems = items?.slice(0, maxVisible) || [];

  return (
    <>
      <div>
        <small className="text-muted d-block mb-2">
          All items will be added for new user
        </small>
        <div className="d-flex flex-wrap gap-2">
          {visibleItems.map((item, index) => {
            const changeType = getChangeType(item.id);
            return (
              <span
                key={item.id || index}
                className={`badge bg-success text-white mr-1 mb-1`}
                style={{
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  padding: "0.25rem 0.5rem",
                }}
              >
                <ChangeIndicator changeType={changeType} showLabel={false} />
                {item.name}
              </span>
            );
          })}
          {items?.length > maxVisible && (
            <span
              className="badge border badge-outline"
              style={{
                cursor: "pointer",
                fontWeight: 600,
                borderStyle: "dashed",
                fontSize: "0.75rem",
                padding: "0.25rem 0.5rem",
              }}
              onClick={() => setModalOpen(true)}
            >
              View All ({items?.length})
            </span>
          )}
        </div>
      </div>

      {/* Modal for viewing all items */}
      <Modal show={modalOpen} onHide={() => setModalOpen(false)} size="lg">
        <Modal.Header closeButton className="background-primary">
          <Modal.Title className="d-flex align-items-center">
            <div
              className="bg-primary rounded-circle d-flex align-items-center justify-content-center mr-2"
              style={{ width: "32px", height: "32px" }}
            >
              <i
                className="fa fa-eye text-white"
                style={{ fontSize: "14px" }}
              ></i>
            </div>
            <span className="color-dark">
              {title} ({items?.length})
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="background-primary">
          <Row
            className="fancy-scroll"
            style={{ maxHeight: "400px", overflow: "auto" }}
          >
            {items?.map((item, index) => {
              const changeType = getChangeType(item.id);
              return (
                <Col xs={12} sm={6} key={item.id || index} className="mb-2">
                  <div className="card card-secondary border mb-0 p-0">
                    <div className="card-body py-1">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <div
                            className="rounded-circle mr-2"
                            style={{
                              width: "8px",
                              height: "8px",
                              backgroundColor: `var(--${color})`,
                            }}
                          ></div>
                          <span
                            className="font-weight-bold color-dark"
                            style={{ fontSize: "0.875rem" }}
                          >
                            {item.name}
                          </span>
                        </div>
                        <ChangeIndicator changeType={changeType} showLabel />
                      </div>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </Modal.Body>
        <Modal.Footer className="background-primary">
          <Button variant="secondary" onClick={() => setModalOpen(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// Resource Group Component
const ResourceGroup = ({ resourceGroup }) => {
  const [expanded, setExpanded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const { resourceType, resourceData = [] } = resourceGroup;

  const primaryItems = resourceData.slice(0, 6);
  const extraItems = resourceData.slice(6);
  const hasExtra = extraItems.length > 0;

  return (
    <Card className="card-primary border mb-3">
      <Card.Body>
        <div className="d-flex flex-column gap-3">
          {/* Resource Type Header */}
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div
                className="bg-primary rounded-circle d-flex align-items-center justify-content-center mr-3"
                style={{ width: "32px", height: "32px" }}
              >
                <i
                  className="fa fa-sitemap text-white"
                  style={{ fontSize: "14px" }}
                ></i>
              </div>
              <div>
                <h6 className="font-weight-bold mb-0 color-dark">
                  {resourceType}
                </h6>
                <small className="text-muted">
                  {resourceData?.length} resources to be assigned
                </small>
              </div>
            </div>
            <div className="d-flex align-items-center">
              <ChangeIndicator changeType="ADDED" />
              {hasExtra && (
                <button
                  className="btn btn-link p-0 ml-2 primary-color"
                  onClick={() => setExpanded(!expanded)}
                >
                  <i
                    className={`fa ${
                      expanded ? "fa-chevron-up" : "fa-chevron-down"
                    }`}
                  ></i>
                </button>
              )}
            </div>
          </div>

          {/* Resource Items */}
          <Row>
            {primaryItems.map((item, index) => (
              <Col
                xs={12}
                sm={6}
                md={4}
                key={item.id ?? `p-${index}`}
                className="mb-2"
              >
                <div
                  className="card card-secondary border mb-0 p-0"
                  style={{ borderLeft: "4px solid #28a745" }}
                >
                  <div className="card-body py-1">
                    <div className="d-flex align-items-center justify-content-between">
                      <span
                        className="font-weight-bold color-dark text-truncate"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {item.accountLabel || `Resource ${item.id}`}
                      </span>
                      <ChangeIndicator changeType="ADDED" showLabel={false} />
                    </div>
                  </div>
                </div>
              </Col>
            ))}

            {/* Collapsible extra items */}
            {hasExtra && (
              <Collapse in={expanded} className="w-100 ml-1">
                <Row className="mt-1">
                  {extraItems.map((item, index) => (
                    <Col
                      xs={12}
                      sm={6}
                      md={4}
                      key={item.id ?? `e-${index}`}
                      className="mb-2"
                    >
                      <div
                        className="card card-secondary border mb-0 p-0"
                        style={{ borderLeft: "4px solid #28a745" }}
                      >
                        <div className="card-body py-1">
                          <div className="d-flex align-items-center justify-content-between">
                            <span
                              className="font-weight-bold color-dark text-truncate"
                              style={{ fontSize: "0.875rem" }}
                            >
                              {item.accountLabel || `Resource ${item.id}`}
                            </span>
                            <ChangeIndicator
                              changeType="ADDED"
                              showLabel={false}
                            />
                          </div>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Collapse>
            )}
          </Row>

          {/* View All Button for large lists */}
          {resourceData.length > 6 && !expanded && (
            <Button
              variant="outline-primary"
              onClick={() => setModalOpen(true)}
              className="align-self-start"
            >
              View All {resourceData.length} Resources
            </Button>
          )}
        </div>
      </Card.Body>

      {/* Modal for viewing all resources */}
      <Modal show={modalOpen} onHide={() => setModalOpen(false)} size="lg">
        <Modal.Header closeButton className="background-primary">
          <Modal.Title className="d-flex align-items-center">
            <div
              className="bg-primary rounded-circle d-flex align-items-center justify-content-center mr-2"
              style={{ width: "32px", height: "32px" }}
            >
              <i
                className="fa fa-eye text-white"
                style={{ fontSize: "14px" }}
              ></i>
            </div>
            <span className="color-dark">
              {resourceType} - All Resources ({resourceData.length})
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="background-primary">
          <Row className="mt-2">
            {resourceData.map((item, index) => (
              <Col
                xs={12}
                sm={6}
                md={4}
                key={item.id ?? `all-${index}`}
                className="mb-3"
              >
                <div
                  className="card card-secondary border mb-0 p-0"
                  style={{ borderLeft: "4px solid #28a745" }}
                >
                  <div className="card-body py-1">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="font-weight-bold color-dark">
                        {item.accountLabel || `Resource ${item.id}`}
                      </div>
                      <ChangeIndicator changeType="ADDED" showLabel />
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Modal.Body>
        <Modal.Footer className="background-primary">
          <Button variant="secondary" onClick={() => setModalOpen(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

// SectionCard Component
const SectionCard = ({
  title,
  icon,
  children,
  count,
  hasChanges = false,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Card className="card-primary border mb-3" style={{ borderRadius: "1rem" }}>
      <Card.Body className="p-4">
        <div className="d-flex flex-column gap-3">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <button
                className="btn btn-link p-0 mr-3"
                onClick={() => setExpanded(!expanded)}
                style={{
                  color: hasChanges ? "var(--warning)" : "var(--primary)",
                }}
              >
                <i
                  className={`fa ${
                    expanded ? "fa-chevron-up" : "fa-chevron-down"
                  }`}
                ></i>
              </button>
              <div
                className={`rounded-circle d-flex align-items-center justify-content-center mr-3 bg-primary`}
                style={{ width: "36px", height: "36px" }}
              >
                <i
                  className={`fa ${icon} text-white`}
                  style={{ fontSize: "14px" }}
                ></i>
              </div>
              <h6 className="font-weight-bold mb-0 color-dark">{title}</h6>
            </div>
            <div className="d-flex align-items-center">
              {count !== undefined && (
                <span
                  className="badge bg-primary text-white"
                  style={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    minWidth: "32px",
                    padding: "0.25rem 0.5rem",
                  }}
                >
                  {count}
                </span>
              )}
            </div>
          </div>
          <Collapse in={expanded} className="mt-1 ml-4">
            <div>{children}</div>
          </Collapse>
        </div>
      </Card.Body>
    </Card>
  );
};

// Access Restriction Alert Component
const AccessRestrictionAlert = ({ isRestricted }) => {
  return (
    <Alert
      variant={isRestricted ? "warning" : "info"}
      className="border-radius-3 mb-4"
    >
      <div className="d-flex align-items-center">
        <i
          className={`fa ${isRestricted ? "fa-lock mr-2" : "fa-unlock mr-2"}`}
          style={{ fontSize: "1.2rem" }}
        ></i>
        <div>
          <h6 className="font-weight-bold mb-1">
            {isRestricted ? "Restricted User Access" : "Standard User Access"}
          </h6>
          <small className="mb-0">
            {isRestricted
              ? "This user has restricted access. Regions, Resources, and Recon Accounts will be assigned with limited permissions."
              : "This user has standard access. All regional, resource, and recon account access will be granted."}
          </small>
        </div>
      </div>
    </Alert>
  );
};

const UserCreation = ({ workQueueData }) => {
  const [tabValue, setTabValue] = useState(0);
  const [processedData, setProcessedData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const processData = async () => {
      try {
        setLoading(true);
        const data = await ProcessUserCreationData(workQueueData);
        setProcessedData(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (workQueueData) {
      processData();
    }
  }, []);

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "200px" }}
      >
        <div className="spinner-border primary-color" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (!processedData) {
    return (
      <Alert variant="warning" className="m-3">
        <i className="fa fa-exclamation-circle mr-2"></i>
        No user data available
      </Alert>
    );
  }

  const {
    userEntity: userData,
    regions = [],
    roles = [],
    team,
    userReports = [],
    systemReports = [],
    userAccess = [],
  } = processedData;

  // Helper functions
  const getStatusColor = (value) => (value ? "text-danger" : "text-success");

  // Create change map for all items (everything is ADDED in creation)
  const createChangeMap = (items) => {
    const map = {};
    items?.forEach((item) => {
      map[item.id] = "ADDED";
    });
    return map;
  };

  const isRestrictedUser = userData?.restrictedUser;

  // User Header Component
  const UserHeader = () => (
    <div
      className="card card-primary border mb-4"
      style={{ borderRadius: "1rem" }}
    >
      <div className="card-body p-4">
        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center">
          <div
            className="bg-primary rounded-circle d-flex align-items-center justify-content-center mr-md-4 mb-3 mb-md-0"
            style={{ width: "64px", height: "64px" }}
          >
            <span
              className="text-white font-weight-bold"
              style={{ fontSize: "1.5rem" }}
            >
              {userData?.firstName?.[0]}
              {userData?.lastName?.[0]}
            </span>
          </div>
          <div className="flex-grow-1">
            <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center mb-2">
              <h4 className="font-weight-bold mb-2 mb-sm-0 mr-3 color-dark">
                {`${userData?.firstName || ""} ${
                  userData?.lastName || ""
                }`.trim()}
              </h4>
              <Badge variant="primary" className="d-flex align-items-center">
                <i className="fa fa-plus mr-1"></i>
                {workQueueData?.workType || "CREATE USER"}
              </Badge>
            </div>
            <div className="d-flex flex-column flex-sm-row flex-wrap align-items-start">
              <div className="d-flex align-items-center mb-1 mb-sm-0 mr-3">
                <i className="fa fa-envelope text-muted mr-1"></i>
                <span className="text-muted">{userData?.username}</span>
              </div>
              <div className="d-flex align-items-center mb-1 mb-sm-0 mr-3">
                <i className="fa fa-id-badge text-muted mr-1"></i>
                <span className="text-muted">{userData?.empCode}</span>
              </div>
              <Badge
                variant={isRestrictedUser ? "warning" : "outline-secondary"}
                className="d-flex align-items-center"
              >
                <i
                  className={`fa ${
                    isRestrictedUser ? "fa-lock mr-1" : "fa-unlock mr-1"
                  }`}
                ></i>
                {isRestrictedUser ? "Restricted Access" : "Standard Access"}
              </Badge>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-100">
      <UserHeader />

      {/* Access Restriction Alert */}
      <AccessRestrictionAlert isRestricted={isRestrictedUser} />

      {/* Summary Card */}
      <Alert
        variant="info"
        className="border-radius-3 mb-4"
        style={{ border: "1px solid var(--info)" }}
      >
        <div className="d-flex flex-column w-100">
          <h5 className="font-weight-bold">User Creation Summary</h5>
          <hr className="my-2" style={{ borderColor: "var(--info)" }} />
          <small className="mb-3">
            This work item will create a new user with the following
            configuration:
          </small>

          <Row className="mt-2">
            <Col xs={12} sm={6} md={3} className="mb-2">
              <div className="d-flex align-items-center">
                <div
                  className="bg-success rounded-circle mr-2"
                  style={{ width: "8px", height: "8px" }}
                ></div>
                <small className="font-weight-bold">
                  {roles?.length} Roles
                </small>
              </div>
            </Col>

            {/* Show Regions only for restricted users */}
            {isRestrictedUser && (
              <Col xs={12} sm={6} md={3} className="mb-2">
                <div className="d-flex align-items-center">
                  <div
                    className="bg-success rounded-circle mr-2"
                    style={{ width: "8px", height: "8px" }}
                  ></div>
                  <small className="font-weight-bold">
                    {regions?.length} Regions
                  </small>
                  <span className="badge badge-warning ml-1">Restricted</span>
                </div>
              </Col>
            )}

            <Col xs={12} sm={6} md={3} className="mb-2">
              <div className="d-flex align-items-center">
                <div
                  className="bg-success rounded-circle mr-2"
                  style={{ width: "8px", height: "8px" }}
                ></div>
                <small className="font-weight-bold">
                  {userReports?.length} User Reports
                </small>
              </div>
            </Col>

            {/* Show System Reports for both, but with different context */}
            {!isRestrictedUser && (
              <Col xs={12} sm={6} md={3} className="mb-2">
                <div className="d-flex align-items-center">
                  <div
                    className="bg-success rounded-circle mr-2"
                    style={{ width: "8px", height: "8px" }}
                  ></div>
                  <small className="font-weight-bold">
                    {systemReports?.length} System Reports
                  </small>
                </div>
              </Col>
            )}

            {/* Show Resources only for restricted users */}
            {isRestrictedUser && (
              <>
                <Col xs={12} sm={6} md={3} className="mb-2">
                  <div className="d-flex align-items-center">
                    <div
                      className="bg-success rounded-circle mr-2"
                      style={{ width: "8px", height: "8px" }}
                    ></div>
                    <small className="font-weight-bold">
                      {userAccess?.length} Resource Types
                    </small>
                    <span className="badge badge-warning ml-1">Restricted</span>
                  </div>
                </Col>

                <Col xs={12} sm={6} md={3} className="mb-2">
                  <div className="d-flex align-items-center">
                    <div
                      className="bg-success rounded-circle mr-2"
                      style={{ width: "8px", height: "8px" }}
                    ></div>
                    <small className="font-weight-bold">
                      {userAccess?.reduce(
                        (total, group) =>
                          total + (group.resourceData?.length || 0),
                        0
                      )}{" "}
                      Recon Accounts
                    </small>
                    <span className="badge badge-warning ml-1">Restricted</span>
                  </div>
                </Col>
              </>
            )}
          </Row>
        </div>
      </Alert>

      {/* Tabs for Main Sections */}
      <Card
        className="card-primary border mt-4"
        style={{ borderRadius: "1rem" }}
      >
        <Card.Header className="bg-transparent border-bottom-0 p-1">
          <Tabs
            activeKey={tabValue}
            onSelect={(k) => setTabValue(Number(k))}
            className="border-bottom"
          >
            <Tab eventKey={0} title="Basic Information" className="border-0" />
            <Tab eventKey={1} title="Details" className="border-0" />
          </Tabs>
        </Card.Header>
        <Card.Body className="p-0">
          {/* Tab 1: Basic Information */}
          {tabValue === 0 && (
            <div className="p-3">
              <SectionCard
                title="User Details"
                icon="fa-user"
                defaultExpanded={true}
              >
                <Row>
                  <DetailItem
                    icon="fa-envelope"
                    label="Email"
                    value={userData?.username}
                    tooltip="Primary login identifier"
                  />
                  <DetailItem
                    icon="fa-id-badge"
                    label="Employee Code"
                    value={userData?.empCode}
                  />
                  <DetailItem
                    icon="fa-user"
                    label="First Name"
                    value={userData?.firstName}
                  />
                  <DetailItem
                    icon="fa-user"
                    label="Middle Name"
                    value={userData?.middleName}
                  />
                  <DetailItem
                    icon="fa-user"
                    label="Last Name"
                    value={userData?.lastName}
                  />
                  <DetailItem
                    icon="fa-briefcase"
                    label="Designation"
                    value={userData?.designation}
                  />
                  <DetailItem
                    icon="fa-phone"
                    label="Mobile Number"
                    value={userData?.mobileNo}
                  />
                  <DetailItem
                    icon="fa-phone"
                    label="Office Phone"
                    value={userData?.officePhone}
                  />
                  <DetailItem
                    icon="fa-building"
                    label="Office Extension"
                    value={userData?.officePhoneExt}
                  />
                  <DetailItem
                    icon="fa-users"
                    label="Team"
                    value={team?.name}
                    tooltip="Assigned team for collaboration and workflow"
                  />
                  <DetailItem
                    icon="fa-lock"
                    label="Restricted User"
                    value={
                      isRestrictedUser
                        ? "Yes - Limited Access"
                        : "No - Full Access"
                    }
                    color={getStatusColor(isRestrictedUser)}
                    status={isRestrictedUser}
                    tooltip={
                      isRestrictedUser
                        ? "Restricted users will have regions, resources, and recon accounts assigned"
                        : "Standard users will not have regions, resources, or recon accounts assigned"
                    }
                  />
                </Row>
              </SectionCard>
            </div>
          )}

          {/* Tab 2: Access & Permissions */}
          {tabValue === 1 && (
            <div className="p-3">
              {/* Roles - Always visible */}
              <SectionCard
                title="Roles & Permissions"
                icon="fa-shield"
                count={roles?.length}
              >
                <AccessChipList
                  items={roles}
                  color="primary"
                  maxVisible={5}
                  title="Roles"
                  changeMap={createChangeMap(roles)}
                />
              </SectionCard>

              {/* Regions - Only for restricted users */}
              {isRestrictedUser ? (
                <SectionCard
                  title="Regions"
                  icon="fa-map-marker"
                  count={regions?.length}
                >
                  <AccessChipList
                    items={regions}
                    color="primary"
                    maxVisible={5}
                    title="Regions"
                    changeMap={createChangeMap(regions)}
                  />
                </SectionCard>
              ) : (
                <Alert variant="info" className="mb-3">
                  <div className="d-flex align-items-center">
                    <i className="fa fa-info-circle mr-2"></i>
                    <div>
                      <h6 className="font-weight-bold mb-1">
                        Regions Not Assigned
                      </h6>
                      <small className="mb-0">
                        Regions are only assigned to restricted users. This user
                        has standard access.
                      </small>
                    </div>
                  </div>
                </Alert>
              )}

              {/* User Reports - Always visible */}
              <SectionCard
                title="User Reports"
                icon="fa-file-text"
                count={userReports?.length}
              >
                <AccessChipList
                  items={userReports}
                  color="primary"
                  maxVisible={5}
                  title="User Reports"
                  changeMap={createChangeMap(userReports)}
                />
              </SectionCard>

              {/* System Reports - Always visible */}
              {!isRestrictedUser ? (
                <SectionCard
                  title="System Reports"
                  icon="fa-file-text"
                  count={systemReports?.length}
                >
                  <AccessChipList
                    items={systemReports}
                    color="primary"
                    maxVisible={5}
                    title="System Reports"
                    changeMap={createChangeMap(systemReports)}
                  />
                </SectionCard>
              ) : (
                <Alert variant="info" className="mb-3">
                  <div className="d-flex align-items-center">
                    <i className="fa fa-info-circle mr-2"></i>
                    <div>
                      <h6 className="font-weight-bold mb-1">
                        System Reports Not Assigned
                      </h6>
                      <small className="mb-0">
                        System reports are only assigned to standard users. This
                        user has restricted access.
                      </small>
                    </div>
                  </div>
                </Alert>
              )}

              {/* Resources & Recon Accounts - Only for restricted users */}
              {isRestrictedUser ? (
                userAccess?.length > 0 ? (
                  <SectionCard
                    title="Resources & Recon Accounts"
                    icon="fa-sitemap"
                    count={userAccess.reduce(
                      (total, group) =>
                        total + (group.resourceData?.length || 0),
                      0
                    )}
                  >
                    <div className="d-flex flex-column gap-3">
                      {userAccess?.map((resourceGroup, index) => (
                        <ResourceGroup
                          key={index}
                          resourceGroup={resourceGroup}
                        />
                      ))}
                    </div>
                  </SectionCard>
                ) : (
                  <Alert variant="warning" className="mb-3">
                    <div className="d-flex align-items-center">
                      <i className="fa fa-exclamation-triangle mr-2"></i>
                      <div>
                        <h6 className="font-weight-bold mb-1">
                          No Resources Assigned
                        </h6>
                        <small className="mb-0">
                          This restricted user does not have any resources or
                          recon accounts assigned.
                        </small>
                      </div>
                    </div>
                  </Alert>
                )
              ) : (
                <Alert variant="info" className="mb-3">
                  <div className="d-flex align-items-center">
                    <i className="fa fa-info-circle mr-2"></i>
                    <div>
                      <h6 className="font-weight-bold mb-1">
                        Resources & Recon Accounts Not Assigned
                      </h6>
                      <small className="mb-0">
                        Resources and recon accounts are only assigned to
                        restricted users. This user has standard access and will
                        not have these restrictions.
                      </small>
                    </div>
                  </div>
                </Alert>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default UserCreation;
